import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { Profile } from '@/types/database';
import { analyticsService } from '@/services/analytics.service';

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;

  // Auth methods
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  fetchProfile: (userId: string) => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

// Module-level guard: auth is initialized exactly once per tab, independent of
// component mount/unmount (a component-owned subscription dies with the first
// component that mounted it, leaving session expiry undetected).
let authInitStarted = false;

/**
 * Initialize auth for the lifetime of the tab. Called once from main.tsx.
 */
export function initAuth(): void {
  if (authInitStarted) return;
  authInitStarted = true;

  const store = useAuthStore;

  // Fallback so a hung getSession can't leave the app on a spinner forever.
  // Cleared as soon as getSession resolves; a late result still applies.
  const fallbackTimer = setTimeout(() => {
    console.warn('[authStore] initAuth: getSession timed out, continuing; session will apply when it resolves');
    store.setState({ loading: false, initialized: true });
  }, 10000);

  supabase.auth
    .getSession()
    .then(({ data: { session }, error }) => {
      clearTimeout(fallbackTimer);
      if (error) console.error('[authStore] initAuth: session error:', error);
      store.setState({ user: session?.user ?? null, loading: false, initialized: true });
      if (session?.user) store.getState().fetchProfile(session.user.id);
    })
    .catch((error) => {
      clearTimeout(fallbackTimer);
      console.error('[authStore] initAuth: error getting session:', error);
      store.setState({ loading: false, initialized: true });
    });

  // IMPORTANT: this callback must stay synchronous. Awaiting Supabase calls
  // inside onAuthStateChange deadlocks the auth client's internal lock (Web
  // Locks — the Safari "Verifying access" hang). All follow-up work is
  // deferred out of the callback.
  supabase.auth.onAuthStateChange((event, session) => {
    store.setState({ user: session?.user ?? null, loading: false, initialized: true });
    setTimeout(() => {
      if (session?.user) {
        store.getState().fetchProfile(session.user.id);
        if (event === 'SIGNED_IN') {
          supabase
            .from('profiles')
            .update({ last_login: new Date().toISOString() })
            .eq('id', session.user.id)
            .then(({ error }) => {
              if (error) console.warn('[authStore] Failed to update last_login:', error);
            });
        }
      } else {
        store.setState({ profile: null });
      }
    }, 0);
  });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: true,
  initialized: false,

  signIn: async (email: string, password: string) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
      // Set user immediately to ensure state is updated
      if (data.user) {
        set({ user: data.user });
        // Fetch profile
        await get().fetchProfile(data.user.id);
        
        // Start analytics session
        analyticsService.startSession(data.user.id, 'email', 'password').catch(err => {
          console.warn('Failed to start analytics session:', err);
        });
      }
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    // End analytics session
    analyticsService.endSession().catch(err => {
      console.warn('Failed to end analytics session:', err);
    });
    
    // Clear local state immediately
    set({ user: null, profile: null, loading: false });
    
    // Set a flag so the Login page knows not to auto-redirect
    sessionStorage.setItem('flow_signing_out', 'true');
    
    // Sign out from Supabase. signOut() reports failure via the returned
    // error (it does not throw), so check it and fall back to a local-scope
    // sign-out — that always clears the persisted session, ensuring a reload
    // cannot silently restore the "signed-out" user.
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('[authStore] signOut: global signOut failed, falling back to local:', error);
        const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
        if (localError) {
          console.warn('[authStore] signOut: local signOut failed too, removing stored session:', localError);
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
              localStorage.removeItem(key);
            }
          }
        }
      }
    } catch (error) {
      console.warn('[authStore] signOut: Supabase signOut threw (non-critical):', error);
    }
    
    // Clear any MSAL/Microsoft cached tokens from localStorage
    // MSAL stores tokens with keys starting with "msal." or containing "login.windows.net"
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('msal.') || 
        key.includes('login.windows.net') || 
        key.includes('login.microsoftonline.com') ||
        key.includes('msal')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Also clear pendingMicrosoftLink and the cached Microsoft identity
    // (written by MicrosoftCallback) so the next user doesn't inherit it
    localStorage.removeItem('pendingMicrosoftLink');
    localStorage.removeItem('microsoftAuth');
    
    // Use full page reload to ensure all auth state is cleared
    window.location.href = '/login';
  },

  fetchProfile: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // Profile doesn't exist or table doesn't exist, which is fine for now
        console.warn('Profile not found (this is okay for development):', error.message);
        set({ profile: null });
        return;
      }

      set({ profile: data });
    } catch (error) {
      console.error('Error fetching profile:', error);
      set({ profile: null });
    }
  },

  updateProfile: async (updates: Partial<Profile>) => {
    const { user } = get();
    if (!user) throw new Error('No user logged in');

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    set({ profile: data as Profile });
  },
}));
